import { Injectable, TemplateRef, ViewContainerRef } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
	ComponentPortal,
	ComponentType,
	Portal,
	TemplatePortal,
} from '@angular/cdk/portal';
import { AuDialogService } from '@core/services/au-dialog.service';
import { ISidebarConfig } from '@layout/au-right-sidebar/right-sidebar.interfaces';
import { iconEnum } from '@app/configs/model/IconEnum';
import { Store } from '@ngrx/store';
import {
	ClearRightSidebarHisory,
	ClearRightSidebarInitialComponent,
} from '@layout/state/layout.actions';
import { setWorkOrderForEditing } from '@app/pages/cmms/components/work-order/state/work-order.actions';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { setIncidentForEditing } from '@app/pages/cmms/components/incident/state/incidents.actions';
import { setRequestForEditing } from '@app/pages/cmms/components/request/state/requests.action';
import { IRightSidebarNavigationInterface } from '@shared/interfaces/right-sidebar-navigation.interface';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { setCmmsAssetForEditing } from '@app/pages/cmms/components/assets/state/assets.actions';
import { IFileEntity } from '@app/pages/cmms/interfaces/files.interfaces';
import {
	setFileForEditing,
	startDeletingFiles,
} from '@app/pages/cmms/components/files/state/files.action';
import { IDataForAttach } from '@layout/au-right-sidebar/right-sidebar.interfaces';
import { IAlertPopoverData } from '@core/services/au-dialog.service';
import { TranslateService } from '@tolgee/ngx';
/**
 * TemplatePortal (which is a Portal that represents an embedded template)
 * requires that a ViewContainerRef is specified for the second argument.
 *
 * To use:
 *
 * component.html
 * ```
 * <button (click)="openRightPanel(tempRef)">Open</button>
 *
 * <ng-template #tempRef>
 *  <mat-toolbar>
 *    Panel toolbar
 *    <span fxFlex></span>
 *    <button mat-icon-button (click)="sidenavService.panel.close()">
 *      <mat-icon>close</mat-icon>
 *    </button>
 *  </mat-toolbar>
 *  <p>Hello, second component!</p>
 * </ng-template>
 * ```
 * component.ts
 * ```
 * export class Component {
 *  constructor(
 *   public sidebarService: AuSidebarService,
 *   private vcr: ViewContainerRef
 *  ) {}
 *
 *  openRightPanel(tempRef: TemplateRef<any>) {
 *    this.sidenavService.setViewContainerRef(this.vcr);
 *    this.sidenavService.setPanelContent(tempRef);
 *    this.sidenavService.open();
 *  }
 *
 *  or
 *
 *  openRightPanel(tempRef: TemplateRef<any>) {
 *    const portal = new TemplatePortal(tempRef, this.vcr);
 *    this.sidebarService.open(portal);
 *  }
 * }
 * ```
 *
 * If you need a popover to prevent data loss inside portal component, provide
 * in opening configuration 'propagateCloseEvent'. Then subscribe in your component
 * to 'sidebarIsClosing$' subject in your component and based on your condition
 * (data changed or not) call 'alertAboutDataLoss' method. If there is no change in data
 * call 'closeAndClearPortal'
 */

@Injectable({
	providedIn: 'root',
})
export class AuRightSidebarService {
	/** We set the reference to sidebar located in layout to use in service */
	sidebar: MatDrawer;
	sidebarConfig$: Observable<ISidebarConfig>;
	sidebarPortal$: Observable<Portal<any>>;
	sidebarFullscreen$: Observable<boolean>;
	sidebarMinimized$: Observable<boolean>;
	sidebarIsClosing$ = new Subject<void>();
	sidebarActionBtnClicked$ = new Subject<void>();
	sidebarActionBtnDisabled$ = new Observable<boolean>();

	private navigationHistory: IRightSidebarNavigationInterface[] = [];

	private pageIndex = 0;

	private initialSidebarConfig: ISidebarConfig = {
		title: { text: '' },
		icon: iconEnum.extension,
		actionBtnName: 'Ok',
		allowFullscreen: false,
		attachmentMenu: false,
		showContentActions: true,
		propagateCloseEvent: false,
		navigation: false,
		fakeNavigation: false,
		noIcon: false,
		disableDefaultScroll: false,
		initialContent: {},
	};

	sidebarConfig = structuredClone(this.initialSidebarConfig);
	/** Data used for popover if propagateClose event set to 'true' and user
	 * should be warned about data loss. Used if not provided directly in alert method
	 */
	private getPopoverData(): IAlertPopoverData {
		return {
			title: this.translateService.instant('sidebar_close_window'),
			description: this.translateService.instant('sidebar_close_window_warning'),
			actionBtnText: this.translateService.instant('close'),
		};
	}

	private viewContainerRef: ViewContainerRef;
	private sidebarPortalSubject = new Subject<Portal<any>>();
	private sidebarFullscreenSubject = new BehaviorSubject(false);
	private sidebarMinimizedSubject = new BehaviorSubject(false);
	private sidebarConfigSubject = new BehaviorSubject(this.sidebarConfig);
	private sidebarActionBtnDisabledSubject = new BehaviorSubject(false);

	constructor(
		private dialogService: AuDialogService,
		private store$: Store,
		private translateService: TranslateService
	) {
		this.sidebarPortal$ = this.sidebarPortalSubject.asObservable();
		this.sidebarFullscreen$ = this.sidebarFullscreenSubject.asObservable();
		this.sidebarMinimized$ = this.sidebarMinimizedSubject.asObservable();
		this.sidebarConfig$ = this.sidebarConfigSubject.asObservable();
		this.sidebarActionBtnDisabled$ = this.sidebarActionBtnDisabledSubject.asObservable();
	}

	setViewContainerRef(vcr: ViewContainerRef): void {
		this.viewContainerRef = vcr;
	}

	setSidebarPortal(portal: Portal<any>): void {
		this.sidebarPortalSubject.next(portal);
	}

	setSidebarContent(componentOrTemplateRef: ComponentType<any> | TemplateRef<any>): void {
		let portal: Portal<any>;

		if (componentOrTemplateRef instanceof TemplateRef) {
			const vcr = this.viewContainerRef ? this.viewContainerRef : null;
			portal = new TemplatePortal(componentOrTemplateRef, vcr);
		} else {
			portal = new ComponentPortal(componentOrTemplateRef);
		}
		this.sidebarPortalSubject.next(portal);
	}

	setComponentConfig(sidebarConfig?: Partial<ISidebarConfig>) {
		this.sidebarConfig = {
			...this.initialSidebarConfig,
			...sidebarConfig,
		};
		this.sidebarConfigSubject.next(this.sidebarConfig);
	}

	setComponentAndOpen(
		componentRef: ComponentType<any>,
		sidebarConfig?: Partial<ISidebarConfig>
	): void {
		if (sidebarConfig) {
			this.setComponentConfig(sidebarConfig);
		}

		const portal = new ComponentPortal(componentRef);
		this.open(portal);
	}

	clearSidebarPortal(): void {
		this.sidebarPortalSubject.next(null);
		this.store$.dispatch(ClearRightSidebarHisory());
	}

	/** Open a portal with optional portal (change to new or open previous) */
	open(portal?: Portal<any>) {
		if (portal) {
			this.sidebarPortalSubject.next(portal);
		}
		return this.sidebar.open();
	}

	toggle() {
		return this.sidebar.toggle();
	}

	minimize() {
		this.toggle();
		this.sidebarMinimizedSubject.next(true);
	}

	undoMinimize() {
		this.toggle();
		this.sidebarMinimizedSubject.next(false);
	}

	fullscreen() {
		this.sidebarFullscreenSubject.next(true);
	}

	undoFullscreen() {
		this.sidebarFullscreenSubject.next(false);
	}

	closeAndClearPortal() {
		this.clearNavigationHistory();
		this.sidebar.close();
		this.clearSidebarPortal();
	}

	clearNavigationHistory() {
		this.clearNavigation();
	}

	close() {
		this.sidebarIsClosing$.next();
		if (this.sidebarConfig.propagateCloseEvent) {
			return;
		}

		this.clearNavigation();

		this.closeAndClearPortal();
		this.store$.dispatch(ClearRightSidebarInitialComponent());
	}

	alertAboutDataLoss(addedData?: IDataForAttach) {
		const data: IAlertPopoverData = this.getPopoverData();
		const dialog = this.dialogService.openAlertDialog(data);

		this.dialogService.closedByEnter(dialog).subscribe(approved => {
			if (!approved) {
				return;
			}

			if (addedData?.files?.length) {
				const pks = addedData.files
					.filter((file: Partial<IFileEntity>) => !file.fileEntityId)
					.map(file => file.pk);

				if (pks.length) {
					this.store$.dispatch(startDeletingFiles({ pks }));
				}
			}
			this.closeAndClearPortal();
		});
	}

	setHomeComponent(data: IRightSidebarNavigationInterface) {
		this.navigationHistory = [data];
		this.pageIndex = this.navigationHistory.length;
	}

	addNewItemToNavigationAndOpen(data: {
		component: ComponentType<any>;
		sidebarConfig: Partial<ISidebarConfig>;
		data?: {
			componentType: CmmsPages;
			entityObject: any;
		};
	}) {
		this.setComponentConfig({ navigation: true });
		this.setComponentDataForLoadingEntityInEditMode(data.data);
		this.navigationHistory = this.navigationHistory.splice(0, this.pageIndex);
		this.navigationHistory.push(data);
		this.pageIndex = this.navigationHistory.length;
		this.openNavigationComponent(data.component, data.sidebarConfig);
	}

	navigateToEditAsset(asset: ICmmsAsset) {
		this.store$.dispatch(setCmmsAssetForEditing({ assetEdit: asset }));
		const config = ACTIVE_PAGE_CONFIG[CmmsPages.editAsset];

		this.addNewItemToNavigationAndOpen({
			component: config.createComponent,
			sidebarConfig: {
				...config.sidebarConfig,
				title: {
					text: config.sidebarConfig.title.text,
					endfix: asset.name,
				},
			},
			data: {
				componentType: CmmsPages.editAsset,
				entityObject: asset,
			},
		});
	}

	navigateToEditFile(file: IFileEntity) {
		this.store$.dispatch(setFileForEditing({ fileEdit: file }));
		const config = ACTIVE_PAGE_CONFIG[CmmsPages.editFile];

		this.addNewItemToNavigationAndOpen({
			component: config.createComponent,
			sidebarConfig: {
				...config.sidebarConfig,
				title: {
					text: config.sidebarConfig.title.text,
					endfix: file.name,
				},
			},
			data: {
				componentType: CmmsPages.editFile,
				entityObject: file,
			},
		});
	}

	navigateToNext() {
		this.pageIndex++;
		this.configureAndOpenNavigationPage();
	}

	navigateToPreviousPage() {
		this.pageIndex = this.pageIndex - 1 <= 0 ? 0 : this.pageIndex - 1;
		this.configureAndOpenNavigationPage();
	}

	navigateToHome() {
		this.pageIndex = 1;
		this.configureAndOpenNavigationPage();
	}

	setComponentDataForLoadingEntityInEditMode(data: {
		componentType: CmmsPages;
		entityObject?: any;
	}) {
		if (data.entityObject) {
			if (data.componentType === CmmsPages.editWorkOrder) {
				this.store$.dispatch(
					setWorkOrderForEditing({ workOrderEdit: data.entityObject })
				);
			} else if (data.componentType === CmmsPages.editRequest) {
				this.store$.dispatch(setRequestForEditing({ requestEdit: data.entityObject }));
			} else if (data.componentType === CmmsPages.editIncident) {
				this.store$.dispatch(setIncidentForEditing({ incidentEdit: data.entityObject }));
			}
		}
	}

	setActionButtonStatus(isDisabled: boolean) {
		this.sidebarActionBtnDisabledSubject.next(isDisabled);
	}

	get isHistoryExisting() {
		return !!this.navigationHistory.length;
	}

	get isNextComponent() {
		return this.pageIndex < this.navigationHistory.length;
	}

	get isPreviousComponent() {
		return this.currentPageIndex > 0;
	}

	get currentPageIndex() {
		return this.pageIndex - 1 >= 0 ? this.pageIndex - 1 : 0;
	}
	//TODO remove this also
	getData() {}

	private clearNavigation() {
		this.navigationHistory = [];
		this.pageIndex = 0;
	}

	private openNavigationComponent(
		componentRef: ComponentType<any>,
		sidebarConfig?: Partial<ISidebarConfig>
	) {
		this.setActionButtonStatus(false);
		if (sidebarConfig) {
			this.setComponentConfig(sidebarConfig);
		}

		const portal = new ComponentPortal(componentRef);
		this.open(portal);
	}

	private configureAndOpenNavigationPage() {
		if (this.navigationHistory[this.currentPageIndex].data) {
			this.setComponentDataForLoadingEntityInEditMode(
				this.navigationHistory[this.currentPageIndex].data
			);
		}
		this.openNavigationComponent(
			this.navigationHistory[this.currentPageIndex].component,
			this.navigationHistory[this.currentPageIndex].sidebarConfig
		);
	}

	viewCurrentEntity(component, page, item: any, current?) {
		this.homeComponent(component, page, current);
		if (item.file) {
			return this.navigateToEditFile(item as IFileEntity);
		}
		if (item.entityType) {
			const id = item.data.workOrderId || item.data.requestId || item.data.incidentId;

			const selectedPage = ACTIVE_PAGE_CONFIG[item.entityType];
			return this.addNewItemToNavigationAndOpen({
				component: ACTIVE_PAGE_CONFIG[item.entityType].createComponent,
				sidebarConfig: {
					...selectedPage.sidebarConfig,
					title: {
						text: selectedPage.sidebarConfig.title.text,
						endfix: id,
					},
				},
				data: {
					componentType: item.entityType,
					entityObject: item.data,
				},
			});
		}
		this.navigateToEditAsset(item as ICmmsAsset);
	}

	homeComponent(component, page, current?) {
		const getId = () => {
			return (
				(current.incidentId || current.workOrderId || current.requestId) ?? current.name
			);
		};
		if (!this.isHistoryExisting) {
			const currentPage = ACTIVE_PAGE_CONFIG[page];

			this.setHomeComponent({
				component: component,
				sidebarConfig: {
					...currentPage.sidebarConfig,
					...(current && {
						title: { text: currentPage.sidebarConfig.title.text, endfix: getId() },
					}),
				},
				data: {
					...(current && { entityObject: current }),
					componentType: page,
				},
			});
		}
	}
}
